/**
 * channel-resolve.js — single source of truth for "which WA channel
 * should this outbound message reply via?"
 *
 * Why this exists (#120): messages-service.js and interactives.js both
 * had their own _getActiveChannelId() helper, with subtly different
 * priority orders. Same chat, two different channels. Subtle bug.
 *
 * Priority (used for ALL outbound surfaces — text, media, interactive,
 * reaction, location, contact):
 *
 *   1. UI-pinned channel ($openChatChannel) — set by loadMessages from
 *      the server's channel_phone_id annotation on the messages
 *      response. This is the most authoritative because it reflects
 *      "the channel this thread is currently on" as the server sees it.
 *
 *   2. Cached chat row's channelPhoneId — most recent channel_id seen
 *      for this chat from the chat-list annotation (#86).
 *
 *   3. Session default phoneNumberId — last resort. Almost certainly
 *      wrong on multi-channel accounts but better than null.
 *
 * S5: removed the prior step-2 "scan open thread for last inbound
 * to_phone" branch. It called getMessages(phone) which is now
 * composite-keyed, and the fallback only fired when $openChatChannel
 * was null — which post-#153 never happens while a thread is open
 * (loadMessages pins the channel on mount). Dead code with active
 * friction against the composite-key refactor.
 */

import { $openChatChannel, $openChatKey } from '../stores/ui.js';
import { $chats } from '../stores/chats.js';
import { getSession } from '../stores/session.js';

export function resolveActiveChannel(phone) {
  // 1. UI-pinned channel
  const fromUi = $openChatChannel.get();
  if (fromUi) return fromUi;

  // 2. Cached chat row channelPhoneId (composite chat_key lookup).
  const openKey = $openChatKey.get();
  if (openKey) {
    const chat = $chats.get()[openKey];
    if (chat?.channelPhoneId) return chat.channelPhoneId;
  }

  // 3. Session default — last resort.
  return getSession()?.phoneNumberId || null;
}
